import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  RotateCcw, Plus, Trash2, Bot, Wand2, ChevronDown, Info,
} from 'lucide-react';
import { SECTION_CATALOG, DEFAULT_AI_HINTS } from '@/lib/resumeTemplateCatalog';

export function FieldGroup({ label, children, htmlFor, hint, inline }) {
  if (inline) {
    return (
      <div className="flex items-center gap-2">
        <label htmlFor={htmlFor} className="text-xs font-medium text-muted-foreground">{label}</label>
        {children}
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="text-xs font-medium text-muted-foreground">{label}</label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function ResetButton({ disabled, onConfirm }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          variant="outline"
          size="default"
          disabled={disabled}
          className="gap-1.5"
          data-testid="resume-template-reset-button"
        >
          <RotateCcw className="h-4 w-4" /> Reset
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="resume-template-reset-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
          <AlertDialogDescription>
            This will revert all edits to the currently active server version. This cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="resume-template-reset-cancel">Keep editing</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} data-testid="resume-template-reset-confirm">
            Discard changes
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function AddSectionPopover({ existingKeys, onAdd }) {
  const [open, setOpen] = useState(false);
  const existing = new Set(existingKeys);
  const available = SECTION_CATALOG.filter((s) => !existing.has(s.key));
  const disabled = available.length === 0;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="default"
          size="sm"
          className="gap-1.5"
          disabled={disabled}
          data-testid="resume-template-add-section-button"
        >
          <Plus className="h-4 w-4" /> Add section
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[360px] p-0"
        data-testid="resume-template-add-section-popover"
      >
        <div className="border-b p-3">
          <p className="text-sm font-semibold">Add a section</p>
          <p className="text-xs text-muted-foreground">
            Pick from the curated catalog. Each section has matching AI extraction + renderer support.
          </p>
        </div>
        <ScrollArea className="max-h-[320px]">
          <ul className="p-2">
            {available.length === 0 && (
              <li className="px-2 py-6 text-center text-xs text-muted-foreground">
                All catalog sections are already in this template.
              </li>
            )}
            {available.map((s) => (
              <li key={s.key}>
                <button
                  type="button"
                  onClick={() => { onAdd(s); setOpen(false); }}
                  className="flex w-full items-start gap-3 rounded-lg p-2 text-left transition-colors hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid={`resume-template-add-section-option-${s.key}`}
                >
                  <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <Plus className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-2 text-sm font-semibold">
                      {s.label}
                      <Badge variant="secondary" className="font-mono text-[10px]">{s.key}</Badge>
                    </p>
                    <p className="text-xs text-muted-foreground">{s.description}</p>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}

export function RemoveSectionButton({ sectionKey, sectionLabel, onConfirm }) {
  return (
    <AlertDialog>
      <Tooltip>
        <TooltipTrigger asChild>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-destructive"
              aria-label={`Remove ${sectionLabel} section`}
              data-testid={`resume-template-section-remove-${sectionKey}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </AlertDialogTrigger>
        </TooltipTrigger>
        <TooltipContent>Remove section</TooltipContent>
      </Tooltip>
      <AlertDialogContent data-testid={`resume-template-section-remove-dialog-${sectionKey}`}>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove &ldquo;{sectionLabel}&rdquo;?</AlertDialogTitle>
          <AlertDialogDescription>
            This section will be removed from the template. The change is local until you click <strong>Save</strong>. You can re-add it later from the catalog.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`resume-template-section-remove-cancel-${sectionKey}`}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            data-testid={`resume-template-section-remove-confirm-${sectionKey}`}
          >
            Remove
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function SectionAIHint({ sectionKey, value, onChange }) {
  const [open, setOpen] = useState(false);
  const isCustom = (value || '').trim().length > 0;
  const defaultHint = DEFAULT_AI_HINTS[sectionKey] || '';
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mt-2">
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-md px-1.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid={`resume-template-section-ai-hint-toggle-${sectionKey}`}
        >
          <Bot className="h-3 w-3 text-primary" />
          AI extraction hint
          {isCustom && (
            <Badge
              variant="secondary"
              className="border border-primary/20 bg-primary/10 px-1 py-0 text-[9px] uppercase tracking-wider text-primary"
            >
              Custom
            </Badge>
          )}
          <ChevronDown
            className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2">
        <div className="rounded-lg border bg-secondary/30 p-2.5">
          <Textarea
            rows={3}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={defaultHint || 'No default hint — describe extraction guidance for the AI parser.'}
            className="text-[12px] leading-relaxed"
            data-testid={`resume-template-section-ai-hint-input-${sectionKey}`}
          />
          <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2">
            <p className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
              <Info className="h-3 w-3" />
              Empty = backend uses built-in default for this section.
            </p>
            <div className="flex items-center gap-2">
              {defaultHint && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 gap-1 px-2 text-[11px]"
                  onClick={() => onChange(defaultHint)}
                  data-testid={`resume-template-section-ai-hint-default-${sectionKey}`}
                >
                  <Wand2 className="h-3 w-3" /> Use default
                </Button>
              )}
              {isCustom && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-[11px] text-muted-foreground"
                  onClick={() => onChange('')}
                  data-testid={`resume-template-section-ai-hint-clear-${sectionKey}`}
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
